import { effect, useSignal } from "@preact/signals";
import { useCallback, useEffect, useMemo, useRef } from "preact/hooks";
import consumer from "../channels/consumer";
import { rollVerdict } from "../dice/feed";
import { feedClient } from "../dice/feed_client";
import { rollKey } from "../dice/landing";
import { memberColor, randomPlayerColor } from "../dice/own_color";
import type { Roll } from "../dice/pool";
import type { FailureReason } from "../dice/roll_and_post";
import { setSheetRollUrl } from "../dice/sheet_roll_url";
import type { Stage } from "../dice/stage";
import { createOutbox } from "../live/outbox";
import { watchSaving } from "../live/saving";
import { createLiveState } from "../live/state";
import { createSync } from "../live/sync";
import type { LiveSheet } from "../types/generated/live";
import "../stylesheets/character_sheet.css";
import { ClassAbilities, RaceAbilities } from "./character_sheet/abilities";
import { Attacks } from "./character_sheet/attacks";
import { Toast } from "./character_sheet/controls";
import { pageStage } from "./character_sheet/dice_stage";
import { BecomeAdventurer, Details, HeroicAction, Notes } from "./character_sheet/detail_boxes";
import { createDrafts } from "./character_sheet/drafts";
import { createEdits, createOutcomes, EditContext, LOAD_KEYS, touches } from "./character_sheet/edits";
import { Header, PanelClose, PanelHeader, type Status } from "./character_sheet/header";
import { Inventory } from "./character_sheet/inventory";
import { interpolate, type Labels } from "./character_sheet/labels";
import { Panel, popupHost, tellBoard, useBlankLinks, useNarrow } from "./character_sheet/panel";
import { type OpenPopup, type Popup, PopupContext } from "./character_sheet/popups";
import { createRoller, type Rolled, rollUrl } from "./character_sheet/roller";
import type { MenuUrls } from "./character_sheet/sheet_menu";
import { sheetMoves } from "./character_sheet/mover";
import { toSheetView } from "./character_sheet/sheet_view";
import { Attributes, CombatStats, HealthPoints } from "./character_sheet/stat_boxes";
import { Loading } from "./ui/loading";
import type { RollListLabels } from "./ui/roll_list";
import { RollContext } from "./ui/roll_chip";

type Props = {
	// The snapshot as the page was built; the sheet draws from it at once and the first sync catches up whatever changed since.
	snapshot?: LiveSheet;
	snapshotUrl: string;
	channel: Record<string, string>;
	labels: Labels;
	// Where this sheet's writes go; only a viewer who may edit gets the controls.
	writeUrl: string;
	notesMaxLength?: number;
	// The popups' sources: a spell's text (characters only, `__spell__` stands for its id), the equipment search, the Oficina.
	spellUrl?: string;
	catalogUrl: string;
	// The Level Up popup's data (characters only), and whether this viewer has the popup, the menu item and the pending band.
	levelUpUrl?: string;
	// The spell picker's lists (characters only): Repouso's tomorrow's spells, "Preparar magias" and the grimório's checklist, for the same viewer.
	spellPrepUrl?: string;
	// A spell the picker offers, previewed whether or not the sheet stores it (`__spell__` stands for its id).
	spellPreviewUrl?: string;
	// The header: the Oficina link for whoever may edit, and the ⋯ menu's PDF, clone and share links.
	editUrl?: string;
	// The Oficina's race step, where an unmade race choice is made.
	choicesUrl?: string;
	// The portrait picker (a character's): the gallery, an upload, and the save, for whoever may edit.
	portraitsUrl?: string;
	portraitUploadUrl?: string;
	portraitSaveUrl?: string;
	menu: MenuUrls;
	// Where a retainer becomes an adventurer (a POST), for whoever may update it.
	becomeAdventurerUrl?: string | null;
	// The campaign's rolls URL (`__campaign__` stands for its id).
	rollsUrl?: string;
	// The Rolagens panel's feed: the campaign's rolls JSON (the same template), the list's words, and the viewer's @handle.
	rollsFeedUrl?: string;
	rollLabels?: RollListLabels;
	viewerHandle?: string | null;
	// "panel" is the sheet inside Mapas' panel: it answers to the board (see `tellBoard`) and links to the full sheet.
	mode?: "panel";
	sheetUrl?: string;
};

async function fetchSnapshot(url: string, signal?: AbortSignal) {
	const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin", cache: "no-store", signal });
	if (!response.ok) throw Object.assign(new Error(`snapshot ${response.status}`), { status: response.status });
	return response.json();
}

const TOAST_MS = 6000;

// The popups are fetched once the sheet is idle, or at the first one opened.
const loadPopupHost = () => import("./character_sheet/popup_host");
const whenIdle = (task: () => void) => ("requestIdleCallback" in window ? window.requestIdleCallback(task) : setTimeout(task, 1500));

// The live sheet: it holds the snapshot, follows the stream, draws the Round 6 layout from it and, for a viewer
// who may edit, writes through the outbox.
export default function CharacterSheet({
	snapshot,
	snapshotUrl,
	channel,
	labels,
	writeUrl,
	notesMaxLength,
	spellUrl,
	catalogUrl,
	levelUpUrl,
	spellPrepUrl,
	spellPreviewUrl,
	editUrl,
	choicesUrl,
	portraitsUrl,
	portraitUploadUrl,
	portraitSaveUrl,
	menu,
	becomeAdventurerUrl,
	rollsUrl,
	rollsFeedUrl,
	rollLabels,
	viewerHandle,
	mode,
	sheetUrl,
}: Props) {
	const inPanel = mode === "panel";
	const state = useMemo(() => createLiveState(snapshot), [snapshot]);
	const status = useSignal<Status>("syncing");
	const saving = useSignal(false);
	const toast = useSignal<string | null>(null);
	const popup = useSignal<OpenPopup | null>(null);
	const stage = useRef<Stage | null>(null);
	const rollsLive = useRef(false);
	// The campaign's feed client while this viewer may roll, which is only where the viewer's colour lives; the Rolagens panel shares it.
	const feed = useRef<ReturnType<typeof feedClient> | null>(null);
	const root = useRef<HTMLElement>(null);
	// Where the board hears the sheet: the panel outlives this island, which a close or a pick unmounts under a roll in flight.
	const panel = useRef<HTMLElement | null>(null);
	useEffect(() => {
		panel.current = root.current?.closest<HTMLElement>("[data-sheet-panel]") ?? root.current;
	}, []);
	const narrow = useNarrow(root) || inPanel;
	useBlankLinks(inPanel);
	// A toast is the board's own in its panel, where the island has no corner of its own.
	const say = useCallback(
		(message: string) => {
			if (inPanel) tellBoard(root.current, "sheet-panel:toast", { message });
			else toast.value = message;
		},
		[inPanel, toast],
	);
	const hostChunk = useSignal<Awaited<ReturnType<typeof loadPopupHost>>["default"] | null>(null);
	const fetchPopupHost = useCallback(
		() =>
			loadPopupHost().then((loaded) => {
				hostChunk.value = loaded.default;
			}),
		[hostChunk],
	);
	const openPopup = useCallback(
		(next: Popup, opener: HTMLElement | null) => {
			const requested = { popup: next, opener };
			popup.value = requested;
			fetchPopupHost().catch(() => {
				// Only the popup still waiting on the chunk fails; one the user closed or replaced meanwhile stays quiet.
				if (popup.value !== requested) return;
				popup.value = null;
				say(labels.edit.rejected);
			});
		},
		[popup, fetchPopupHost, say, labels.edit.rejected],
	);
	const closePopup = useCallback(() => {
		popup.value = null;
	}, [popup]);
	const sync = useRef<ReturnType<typeof createSync> | undefined>(undefined);
	const outcomes = useMemo(createOutcomes, []);
	const drafts = useMemo(createDrafts, []);
	const outbox = useMemo(
		() =>
			createOutbox({
				state,
				storageKey: `live-outbox:${channel.record_type}:${channel.id}`,
				onSettle: outcomes.settled,
				onReject: ({ mutationId, reason }: { mutationId: string; reason: string | null }) => {
					outcomes.rejected({ mutationId });
					say(reason || labels.edit.rejected);
				},
				onReplay: (replay: { mutationId: string }) => {
					outcomes.replayed(replay);
					sync.current?.resync();
				},
			}),
		[state, channel, labels, say, outcomes],
	);
	const edits = useMemo(
		() =>
			createEdits({
				outbox,
				outcomes,
				url: writeUrl,
				view: () => toSheetView(state.view.peek()),
				refuse: () => say(labels.edit.coins_refused),
			}),
		[outbox, outcomes, writeUrl, state, say, labels],
	);

	const roller = useMemo(() => {
		const current = () => toSheetView(state.view.peek());
		const url = () => rollUrl(rollsUrl, current()?.meta);
		const name = () => current()?.sheet.name ?? "";
		// In Mapas the board's own card plays the roll, its announcer speaks it and its toast reports a failure: a posted
		// roll is handed over as it starts, as the board's d20 does, so the feed's copy of it plays nothing more.
		const board = (event: string, detail: object) => tellBoard(panel.current, `dice-roller:${event}`, { name: name(), ...detail });
		const show = ({ outcome, description }: Rolled) => {
			if (inPanel) {
				if (outcome.kind === "secret") board("secret", { roll: outcome.roll });
				else if (outcome.kind === "local") board("local", { roll: outcome.roll, description });
				else if (outcome.kind === "failed") board("failed", { roll: outcome.roll, reason: outcome.reason });
				return;
			}
			if (outcome.kind === "failed") {
				toast.value = labels.dice_failed[outcome.reason];
				return;
			}
			const note = outcome.kind === "posted" ? null : outcome.kind === "secret" ? labels.dice_roller.secret_title : labels.roll.local_note;
			const data = {
				name: name(),
				notation: outcome.roll.notation,
				result: outcome.roll.result,
				description: description || null,
				note,
				verdict: rollVerdict(outcome.roll),
				color: current()?.meta.campaign_id
					? memberColor(feed.current?.state.members ?? null, feed.current?.state.viewerAccountId ?? null)
					: randomPlayerColor(),
			};
			if (outcome.kind === "posted" && rollsLive.current) stage.current?.throwOnly(data, { key: rollKey(data), own: true });
			else stage.current?.show(data, { key: rollKey(data), own: true });
		};
		return {
			show,
			roller: createRoller({
				url,
				subject: () => (channel.record_type === "retainer" ? { retainer_id: channel.id } : { character_id: channel.id }),
				names: labels.dice_bar.adjustment,
				title: (spec) => interpolate(labels.roll.roll_title, { description: spec.description }),
				show,
				rolling: inPanel ? (roll, description) => board("rolling", { roll, description }) : undefined,
			}),
		};
	}, [state, channel, labels, rollsUrl, toast, inPanel, panel, rollsLive]);

	const view = toSheetView(state.view.value);

	// The dice bar posts where the live snapshot lets this viewer, as the chips do, read when it rolls.
	useEffect(() => {
		if (inPanel) return undefined;
		setSheetRollUrl(() => rollUrl(rollsUrl, toSheetView(state.view.peek())?.meta));
		return () => setSheetRollUrl(null);
	}, [rollsUrl, state, inPanel]);

	const feedCampaign = !inPanel && rollsFeedUrl ? (view?.meta.campaign_id ?? null) : null;
	useEffect(() => {
		if (!feedCampaign || !rollsFeedUrl) return undefined;
		const client = feedClient(feedCampaign, { url: rollsFeedUrl.replace("__campaign__", encodeURIComponent(feedCampaign)), handle: viewerHandle ?? null });
		feed.current = client;
		return () => {
			client.release();
			feed.current = null;
		};
	}, [feedCampaign, rollsFeedUrl, viewerHandle]);

	// Where a roll plays; the page renders it, and the island never fills it.
	useEffect(() => {
		if (inPanel) return undefined;
		stage.current = pageStage();
		return () => {
			stage.current?.clear();
			stage.current = null;
		};
	}, [inPanel]);

	// The dice bar rolls for this sheet too: its results land on the same cards.
	useEffect(() => {
		if (inPanel) return undefined;
		const listen = (kind: "posted" | "local" | "secret") => (event: Event) => {
			const { roll } = (event as CustomEvent<{ roll: Roll }>).detail;
			roller.show({ outcome: { kind, roll }, description: "" });
		};
		const failed = (event: Event) => {
			const { roll, reason } = (event as CustomEvent<{ roll: Roll; reason: FailureReason }>).detail;
			roller.show({ outcome: { kind: "failed", roll, reason }, description: "" });
		};
		const handlers: [string, (event: Event) => void][] = [
			["dice-roller:rolled", listen("posted")],
			["dice-roller:local", listen("local")],
			["dice-roller:secret", listen("secret")],
			["dice-roller:failed", failed],
		];
		for (const [name, handler] of handlers) document.addEventListener(name, handler);
		return () => {
			for (const [name, handler] of handlers) document.removeEventListener(name, handler);
		};
	}, [roller, inPanel]);

	useEffect(() => {
		const live = createSync({
			state,
			outbox,
			accepts: (message: { record_type?: string; sheet_id?: string }) => message.record_type === channel.record_type && message.sheet_id === channel.id,
			subscribe: (callbacks: object) => consumer.subscriptions.create(channel, callbacks),
			fetchSnapshot: ({ signal }: { signal?: AbortSignal }) => fetchSnapshot(snapshotUrl, signal),
		});
		sync.current = live;
		outbox.restore();
		const watch = watchSaving({ pending: state.pending, after: 500 });
		const stopMirror = effect(() => {
			status.value = live.status.value as Status;
			saving.value = watch.saving.value;
		});
		return () => {
			stopMirror();
			watch.dispose();
			drafts.flush();
			outbox.destroy();
			live.destroy();
		};
	}, [state, outbox, drafts, snapshotUrl, channel, status, saving]);

	const drawn = Boolean(view);
	useEffect(() => {
		if (drawn) whenIdle(() => void fetchPopupHost().catch(() => {}));
	}, [drawn, fetchPopupHost]);

	useEffect(() => {
		if (!toast.value) return undefined;
		const timer = setTimeout(() => {
			toast.value = null;
		}, TOAST_MS);
		return () => clearTimeout(timer);
	}, [toast.value, toast]);

	const loadBusy = touches(state.pending.value, LOAD_KEYS);
	const locale = document.documentElement.lang || "pt-BR";
	const retainer = view?.record_type === "retainer";
	const PopupHost = hostChunk.value;
	return (
		<section
			ref={root}
			class="cs-root w-full text-ink"
			data-theme={view?.meta.theme ?? undefined}
			data-live-version={state.version.value}
			data-live-status={status.value}
		>
			{view ? (
				<EditContext.Provider value={view.meta.can_update ? edits : null}>
					<PopupContext.Provider value={openPopup}>
						<RollContext.Provider value={roller.roller}>
							{inPanel ? (
								<PanelHeader
									view={view}
									status={status.value}
									saving={saving.value}
									labels={labels}
									sheetUrl={sheetUrl}
									onClose={() => tellBoard(root.current, "sheet-panel:close")}
								/>
							) : (
								<Header
									view={view}
									status={status.value}
									saving={saving.value}
									labels={labels}
									locale={locale}
									feed={Boolean(rollsUrl && view.meta.campaign_id)}
									editUrl={editUrl}
									menu={menu}
									picker={Boolean(portraitsUrl && portraitUploadUrl && portraitSaveUrl)}
								/>
							)}
							<div class="mx-auto flex max-w-screen-xl flex-col gap-2 px-2 pb-4 pt-2">
								<div class="cs-top">
									<Attributes view={view} labels={labels} />
									<HealthPoints view={view} labels={labels} />
								</div>
								<CombatStats view={view} labels={labels} />
								<div class="cs-pair">
									<Attacks view={view} labels={labels} />
									{retainer ? (
										<div class="flex flex-col gap-2">
											<RaceAbilities view={view} labels={labels} choicesUrl={choicesUrl} />
											<HeroicAction view={view} labels={labels} />
										</div>
									) : (
										<RaceAbilities view={view} labels={labels} choicesUrl={choicesUrl} />
									)}
								</div>
								{!retainer && <ClassAbilities view={view} labels={labels} choicesUrl={choicesUrl} />}
								<Inventory
									view={view}
									labels={labels}
									locale={locale}
									loadBusy={loadBusy}
									notify={say}
									move={sheetMoves(view.meta.campaign_id, labels, edits)}
								/>
								{!retainer && <Details view={view} labels={labels} />}
								<Notes view={view} labels={labels} maxLength={notesMaxLength} drafts={drafts} />
								{retainer && !inPanel && becomeAdventurerUrl && view.meta.can_update && (
									<BecomeAdventurer view={view} labels={labels} url={becomeAdventurerUrl} />
								)}
							</div>
							{popup.value && PopupHost && (
								<PopupHost
									current={popup.value}
									onClose={closePopup}
									view={view}
									labels={labels}
									locale={locale}
									narrow={narrow}
									host={inPanel ? popupHost(root.current) : null}
									urls={{
										spell: spellUrl ?? null,
										catalog: catalogUrl,
										levelUp: levelUpUrl ?? null,
										spellPrep: spellPrepUrl ?? null,
										spellPreview: spellPreviewUrl ?? null,
										portrait:
											portraitsUrl && portraitUploadUrl && portraitSaveUrl ? { list: portraitsUrl, upload: portraitUploadUrl, save: portraitSaveUrl } : null,
									}}
									feed={rollsFeedUrl && rollLabels ? { url: rollsFeedUrl, handle: viewerHandle ?? null, labels: rollLabels, live: rollsLive } : null}
									notify={say}
									refresh={() => sync.current?.resync()}
								/>
							)}
							{popup.value && !PopupHost && (
								// Until the chunk arrives: a shell that Escape and the close button dismiss, so the real popup never turns up late.
								<Panel
									title={labels.status.syncing}
									icon="hourglass"
									closeLabel={labels.panel.close}
									narrow={narrow}
									host={inPanel ? popupHost(root.current) : null}
									opener={popup.value.opener}
									onClose={closePopup}
								>
									<Loading compact label={labels.status.syncing} />
								</Panel>
							)}
						</RollContext.Provider>
					</PopupContext.Provider>
				</EditContext.Provider>
			) : inPanel ? (
				<div class="relative">
					<Loading compact busy={status.value === "syncing"} label={labels.status[status.value]} />
					<div class="absolute right-1 top-1">
						<PanelClose labels={labels} onClose={() => tellBoard(root.current, "sheet-panel:close")} />
					</div>
				</div>
			) : (
				<Loading busy={status.value === "syncing"} label={labels.status[status.value]} />
			)}
			{!inPanel && toast.value && <Toast message={toast.value} />}
		</section>
	);
}
